import { FC } from 'react';
import {
  Button,
  ButtonProps,
  Divider,
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { BiCollection, BiSelectMultiple, BiX } from 'react-icons/bi';
import {
  MdDownload,
  MdDriveFolderUpload,
  MdOutlineCloudCircle,
  MdOutlineCreateNewFolder,
  MdUploadFile,
} from 'react-icons/md';
import { Link } from 'react-router-dom';

import { FileMoveAction } from '../../../components/file-details/file-details-actions/file-move-action/file-move-action';

import { isFile } from '../../../graphql/utils/utils';
import { UsePaginationBag } from '../../../hooks/use-pagination/use-pagination';

import { SelectedFilesState } from '../../../pages/file-browser-page/use-selected-files';
import { pluralize } from '../../../utils/pluralize/pluralize';

type Props = {
  cedantId: string;
  isUploading: boolean;
  isDownloadingFolder?: boolean;
  onUploadFile: () => void;
  onCreateFolder: () => void;
  onDownloadFolder: () => void;
  onUploadFolder: () => void;
  pagination: UsePaginationBag;
  onOpenActivityFeed: () => void;
  canCreate: boolean;

  isMultiSelectEnabled: boolean;
  toggleMultiSelect: () => void;
  selectedFiles: SelectedFilesState;
  selectAllFiles: () => void;
  clearAllFiles: () => void;
};

export const Toolbar: FC<Props> = ({
  cedantId,
  pagination,
  isUploading,
  isDownloadingFolder = false,
  onUploadFile,
  onUploadFolder,
  onCreateFolder,
  onDownloadFolder,
  onOpenActivityFeed,
  canCreate,
  isMultiSelectEnabled,
  toggleMultiSelect,
  selectedFiles,
  selectAllFiles,
  clearAllFiles,
}) => {
  const hasSelectedFiles =
    selectedFiles && Object.keys(selectedFiles).length > 0;

  const selectedFilesLength = selectedFiles
    ? Object.keys(selectedFiles).length
    : 0;

  const actionableFiles = Object.values(pagination.page).filter(
    (object) => object.__typename === 'File' && object.canMove
  );
  return (
    <Stack data-testid="file-browser-toolbar">
      <HStack>
        {canCreate && (
          <HStack spacing={6}>
            <Button
              p={0}
              variant="clear"
              _hover={{ color: 'blue.700' }}
              disabled={isUploading}
              onClick={onUploadFile}
              leftIcon={<MdUploadFile size="22" />}
              data-testid="upload-file-button"
            >
              Upload File
            </Button>
            <Button
              p={0}
              variant="clear"
              _hover={{ color: 'blue.700' }}
              disabled={isUploading}
              onClick={onUploadFolder}
              leftIcon={<MdDriveFolderUpload size="22" />}
              data-testid="upload-folder-button"
            >
              Upload Folder
            </Button>
            <Button
              p={0}
              variant="clear"
              _hover={{ color: 'blue.700' }}
              onClick={onCreateFolder}
              leftIcon={<MdOutlineCreateNewFolder size="22" />}
              data-testid="create-folder-button"
            >
              Create Folder
            </Button>
            <Button
              p={0}
              variant="clear"
              _hover={{ color: 'blue.700' }}
              onClick={onDownloadFolder}
              disabled={isDownloadingFolder}
              isLoading={isDownloadingFolder}
              leftIcon={<MdDownload size="22" />}
              data-testid="download-folder-button"
            >
              Download Folder
            </Button>

            {actionableFiles.length > 1 && (
              <>
                <Divider
                  orientation="vertical"
                  borderWidth="1px"
                  borderColor="gray.300"
                  height="20px"
                />
                <Button
                  p={0}
                  variant="clear"
                  _hover={{ color: 'blue.700' }}
                  onClick={toggleMultiSelect}
                  leftIcon={<BiSelectMultiple size="20" />}
                  data-testid="bulk-actions-button"
                >
                  Select Multiple
                </Button>
              </>
            )}
          </HStack>
        )}
        <Spacer />
        <>
          <Tooltip
            fontSize="xs"
            placement="bottom"
            label="Distributed ledger network"
          >
            <span>
              <IconButton
                as={Link}
                variant="clear"
                icon={<MdOutlineCloudCircle size="22" />}
                aria-label="distributed ledger network"
                to={`/cedant/${cedantId}/dlt-nodes`}
              />
            </span>
          </Tooltip>
          <Tooltip fontSize="xs" placement="bottom" label="File activity">
            <span>
              <IconButton
                variant="clear"
                icon={<BiCollection size="22" />}
                aria-label="file activty"
                onClick={onOpenActivityFeed}
                data-testid="open-file-activity"
              />
            </span>
          </Tooltip>

          {pagination.totalItemCount > 0 && (
            <Text textStyle="subtitle" data-testid="current-page-number">
              Page {pagination.currentPageNumber} of{' '}
              {pagination.totalItemCount.toLocaleString()}{' '}
              {pagination.totalItemCount === 1 ? 'item' : 'items'}
            </Text>
          )}
        </>
      </HStack>
      {isMultiSelectEnabled && !hasSelectedFiles && (
        <HStack
          px={6}
          py={4}
          bg={'gray.100'}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          data-testid="bulk-actions-toolbar"
        >
          <Text pr={6} fontWeight="medium">
            Select files
          </Text>
          <ToolbarButton onClick={selectAllFiles}>Select all</ToolbarButton>
          <CloseButton onClick={toggleMultiSelect} />
        </HStack>
      )}
      {isMultiSelectEnabled && hasSelectedFiles && (
        <HStack
          px={6}
          py={4}
          spacing={4}
          bg={'blue.50'}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          data-testid="bulk-actions-toolbar"
        >
          <Text pr={6} fontWeight="medium">
            {selectedFilesLength} {pluralize('file', selectedFilesLength)}{' '}
            selected
          </Text>
          <FileMoveAction
            files={Object.values(selectedFiles).filter(isFile)}
            onMoveComplete={toggleMultiSelect}
            buttonType="Button"
            colorScheme="blue"
          />
          <ToolbarButton onClick={clearAllFiles}>Deselect all</ToolbarButton>
          <CloseButton onClick={toggleMultiSelect} />
        </HStack>
      )}
    </Stack>
  );
};

const ToolbarButton: FC<ButtonProps> = (props) => (
  <Button
    variant="ghost"
    colorScheme="blue"
    _hover={{ bg: 'gray.200' }}
    {...props}
  />
);

const CloseButton: FC<ButtonProps> = (props) => (
  <Button
    variant="ghost"
    colorScheme="gray"
    _hover={{ bg: 'gray.200' }}
    leftIcon={<BiX size={20} />}
    {...props}
  >
    Close
  </Button>
);